import { PostHogProvider as PHProvider, PostHogErrorBoundary, usePostHog } from "@posthog/react";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useEffect } from "react";

import { env } from "@/env/client";
import type { User } from "@/lib/auth";

const isPostHogEnabled = import.meta.env.PROD && !!env.VITE_POSTHOG_KEY;
const posthogApiKey = env.VITE_POSTHOG_KEY;

const options = {
	api_host: "https://us.i.posthog.com",
	defaults: "2026-01-30",
	autocapture: true,
	capture_exceptions: true,
	capture_pageview: false,
	capture_pageleave: true,
	session_recording: {
		recordCrossOriginIframes: false,
	},
} as const;

function PostHogAuthSync() {
	const ph = usePostHog();
	const queryClient = useQueryClient();
	const user = queryClient.getQueryData<{ user?: User | null } | undefined>(["user"])?.user;

	useEffect(() => {
		if (!ph) return;

		if (user) {
			ph.identify(user.id, {
				name: user.name,
				email: user.email,
				is_anonymous: user.isAnonymous,
			});
		}
	}, [ph, user]);

	return null;
}

function PostHogPageview() {
	const ph = usePostHog();
	const router = useRouter();

	useEffect(() => {
		if (!ph) return;

		const unsubscribe = router.subscribe("onResolved", () => {
			ph.capture("$pageview");
		});

		return unsubscribe;
	}, [ph, router]);

	return null;
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
	if (!isPostHogEnabled || !posthogApiKey) {
		return children;
	}

	return (
		<PHProvider apiKey={posthogApiKey} options={options}>
			<PostHogAuthSync />
			<PostHogPageview />
			<PostHogErrorBoundary>{children}</PostHogErrorBoundary>
		</PHProvider>
	);
}
